/**
 * ScamBlocker AI — Auth Guard
 * Korumalı sayfalar için kimlik doğrulama kontrolü.
 * Token expired eventi dinler, idle timeout uygular.
 */

import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router';
import apiClient, { getAccessToken, getRefreshToken } from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';
import type { MeResponse } from '@/shared/lib/api/types';
import { useAuthStore } from '@/shared/stores/authStore';

const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 dakika

interface Props {
  children: ReactNode;
}

export function AuthGuard({ children }: Props) {
  const { isAuthenticated, isLoading, logout, login } = useAuthStore();
  const location = useLocation();
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialized = useRef(false);

  // İlk yükleme: /me ile oturum kontrolü
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const checkSession = async () => {
      // Token yoksa /me'yi hiç deneme: login sayfasında boşuna 401 üretiyordu.
      if (!getAccessToken()) {
        logout();
        return;
      }
      try {
        const { data } = await apiClient.get<MeResponse>(API.org.me);
        const { staff, settings: _settings } = data;
        login(
          {
            id: staff.id,
            email: staff.email,
            full_name: staff.full_name,
            role: staff.role,
            permissions: staff.permissions,
            last_login_at: staff.last_login_at ?? null,
          },
          {
            id: staff.org_id,
            name: staff.name,
            plan_code: staff.plan_code,
            // 2026-08-27: type maplenmiyordu — /me yanıtı login'in yazdığı org'u
            // type'sız EZİYOR, Okul menüsü org.type!=='school' diye gizleniyordu.
            type: (staff as { type?: string }).type,
            seats: (staff as { seats?: number }).seats,
          },
          // 2026-08-27: burada '' geçmek setTokens('','') ile canlı token'ları
          // SİLİYORDU — girişten hemen sonra tüm istekler imzasız kalıyordu.
          getAccessToken() ?? '',
          getRefreshToken() ?? '',
        );
      } catch {
        logout();
      }
    };

    checkSession();
  }, [login, logout]);

  // Auth expired event dinle
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener('auth:expired', handler);
    return () => window.removeEventListener('auth:expired', handler);
  }, [logout]);

  // Idle timeout
  useEffect(() => {
    if (!isAuthenticated) return;

    const resetTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        logout();
      }, IDLE_TIMEOUT);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    for (const e of events) window.addEventListener(e, resetTimer, { passive: true });
    resetTimer();

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      for (const e of events) window.removeEventListener(e, resetTimer);
    };
  }, [isAuthenticated, logout]);

  if (isLoading) {
    return (
      <div className="center" style={{ minHeight: '100dvh' }}>
        <div className="animate-spin" style={{ width: 32, height: 32 }}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-primary)"
            strokeWidth="2.5"
            aria-hidden="true"
          >
            <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
